import { Avatar, Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

const COURSE_BOOKS_BY_LEVEL = {
  A1: [
    "Spektrum A1+",
    "Grammatik aktiv",
    "Hören und Sprechen A1",
    "Lesen und Schreiben A1",
  ],
  A2: [
    "Spektrum A2+",
    "Grammatik aktiv",
    "Hören und Sprechen A2",
    "Lesen und Schreiben A2",
  ],
  B1: [
    "Spektrum B1+",
    "Grammatik aktiv",
    "Hören und Sprechen B1",
    "Lesen und Schreiben B1",
  ],
  B2: [
    "Spektrum B2",
    "Grammatik aktiv (B2/C1)",
    "Hören und Sprechen B2",
    "Lesen und Schreiben B2",
  ],
  C1: [
    "Erkundungen C1",
    "Grammatik aktiv (B2/C1)",
    "Hören und Sprechen C1",
    "Lesen und Schreiben C1",
  ],
};

const CourseInfoBox = ({
  dayTimes = [],
  formattedStartDate = "-",
  formattedEndDate = "-",
  duration = null,
  enrolledStudents = null,
  maxStudents = null,
  courseLevel = "",
  requiredLevelText = "",
  audienceType = "",
  minimumAge = null,
  maximumAge = null,
  specialCourseCustomization = false,
  compact = false,
  teacherName = "",
  teacherPhotoSrc = "",
  hasTeacherPhoto = false,
  teacherFirstName = "",
  teacherLastName = "",
  onTeacherImageError,
  onTeacherClick,
}) => {
  const { t, i18n } = useTranslation(["ui"]);
  const isArabic = i18n.language === "ar";
  const textAlign = isArabic ? "right" : "left";
  const hasDuration =
    duration !== null && duration !== undefined && String(duration).trim() !== "";
  const hasCapacity =
    enrolledStudents !== null && enrolledStudents !== undefined &&
    maxStudents !== null && maxStudents !== undefined;
  const remainingStudents = hasCapacity
    ? Math.max(0, Number(maxStudents) - Number(enrolledStudents))
    : null;
  const normalizedCourseLevel =
    String(courseLevel).trim().match(/^[A-C][0-2]/i)?.[0]?.toUpperCase() || "";
  const courseBooks = COURSE_BOOKS_BY_LEVEL[normalizedCourseLevel] || [];
  const audienceLabel = audienceType
    ? t(`courseDetails.audience.${String(audienceType).toLowerCase()}`, { defaultValue: audienceType })
    : "";
  const hasSpecialCourseInfo = Boolean(requiredLevelText || audienceLabel || (minimumAge != null && maximumAge != null));
  const audienceDetails = hasSpecialCourseInfo ? (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 1.2 }}>
      {requiredLevelText && <Box>
        <Typography fontSize={{ xs: 12, sm: 12, md: 13 }} fontWeight={800} color="#3A321B">{t("courseDetails.requiredLevel", { defaultValue: "Required level" })}</Typography>
        <Typography fontSize={{ xs: 12, sm: 13, md: 15 }} color="#333">{requiredLevelText}</Typography>
      </Box>}
      {audienceLabel && <Box>
        <Typography fontSize={{ xs: 12, sm: 12, md: 13 }} fontWeight={800} color="#3A321B">{t("courseDetails.audience.label", { defaultValue: "Audience" })}</Typography>
        <Typography fontSize={{ xs: 12, sm: 13, md: 15 }} color="#333">{audienceLabel}</Typography>
      </Box>}
      {minimumAge != null && maximumAge != null && <Box>
        <Typography fontSize={{ xs: 12, sm: 12, md: 13 }} fontWeight={800} color="#3A321B">{t("courseDetails.audience.ageRange", { defaultValue: "Age range" })}</Typography>
        <Typography fontSize={{ xs: 12, sm: 13, md: 15 }} color="#333">{minimumAge}–{maximumAge}</Typography>
      </Box>}
    </Box>
  ) : null;

  const formatDay = (day) =>
    String(day)
      .toLowerCase()
      .replace(/^\w/, (c) => c.toUpperCase())
      .slice(0, 3);

  const scheduleRows =
    Array.isArray(dayTimes) && dayTimes.length > 0
      ? dayTimes.map((dt) => ({
          day: formatDay(dt.dayOfWeek),
          time: dt.startTime && dt.endTime ? `${dt.startTime} - ${dt.endTime}` : "-",
        }))
      : [];

  const daysText =
    scheduleRows.length > 0
      ? scheduleRows.map((row) => row.day).join(", ")
      : "Monday and Tuesday";

  return (
    <Box
      sx={{
        flex: 1,
        background: "#FFF9E6",
        borderRadius: "12px",
        p: compact ? { xs: 2, sm: 2.5, md: 3 } : { xs: 1.5, sm: 2, md: 3.5 },
        width: "100%",
        minWidth: compact ? "auto" : { xs: "auto", sm: "auto", md: 300 },
        maxWidth: compact ? "100%" : { xs: "100%", sm: 420 },
        alignSelf: { xs: "stretch", md: "auto" },
        display: "flex",
        flexDirection: "column",
        alignItems: { xs: "stretch", sm: "center" },
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
      }}
    >
      <Box sx={{ flex: 1, width: "100%" }}>
        <Typography
          fontWeight={700}
          sx={{
            fontSize: compact ? { xs: 16, sm: 18, md: 20 } : { xs: 16, sm: 18, md: 22 },
            textAlign: "center",
            color: "#111",
          }}
          mb={compact ? 2 : 3}
        >
          {t("courseDetails.infoBox.title")}
        </Typography>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row-reverse" },
          alignItems: { xs: "stretch", md: "stretch" },
          gap: { xs: 2.5, md: 2 },
          width: "100%",
          direction: "ltr",
        }}
      >
      <Box
        sx={{
          width: { xs: "100%", md: "auto" },
          flex: "1 1 auto",
          direction: isArabic ? "rtl" : "ltr",
          display: "grid",
          gridTemplateColumns: compact
            ? { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", md: "repeat(5, minmax(0, 1fr))" }
            : { xs: "1fr", sm: "1fr 1fr" },
          columnGap: compact ? { xs: 2, sm: 2.5, md: 1.25 } : { xs: 2, sm: 2 },
          rowGap: compact ? { xs: 2, sm: 2.25, md: 1.5 } : { xs: 2, sm: 2 },
          overflow: "hidden",
          border: "1px solid #E8D89A",
          borderRadius: "12px",
          backgroundColor: "rgba(255,255,255,0.62)",
          "& > .course-info-cell": {
            minWidth: 0,
            px: { xs: 2, md: 1.25 },
            py: { xs: 1.5, md: 1.75 },
            alignItems: "center",
            justifyContent: "flex-start",
            textAlign: "center",
            borderBottom: { xs: "1px solid #EEE4BE", md: "none" },
            borderInlineEnd: { xs: "none", md: "1px solid #EEE4BE" },
          },
          "& > .course-info-cell:last-of-type": {
            borderBottom: "none",
          },
        }}
      >
        {/* Days */}
        <Box className="course-info-cell" sx={{ display: "flex", flexDirection: "column", gap: 0.8 }}>
          <Typography
            fontWeight={800}
            fontSize={{ xs: 12, sm: 13, md: 14 }}
            color="#3A321B"
            sx={{ textAlign: "center" }}
          >
            {t("courseDetails.infoBox.daysLabel")}
          </Typography>
          <Typography
            fontSize={{ xs: 12, sm: 13, md: 16 }} // Reduced sm font size
            fontWeight={400}
            sx={{ color: "#333", textAlign: "center", width: "100%" }}
          >
            {scheduleRows.length > 0 ? (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "auto 1fr",
                  columnGap: 1,
                  rowGap: 0.5,
                  justifyContent: "center",
                  width: "fit-content",
                  mx: "auto",
                }}
              >
                {scheduleRows.map((row, idx) => (
                  <Box
                    key={`${row.day}-${row.time}-${idx}`}
                    sx={{ display: "contents" }}
                  >
                    <Typography
                      fontSize={{ xs: 12, sm: 13, md: 16 }}
                      fontWeight={400}
                      sx={{ color: "#333", textAlign: "center" }}
                    >
                      {row.day}
                    </Typography>
                    <Typography
                      fontSize={{ xs: 12, sm: 13, md: 16 }}
                      fontWeight={500}
                      sx={{ color: "#333", textAlign: "center" }}
                    >
                      {row.time}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              daysText
            )}
          </Typography>
          <Typography
            fontSize={{ xs: 10, sm: 11, md: 12 }}
            fontWeight={600}
            sx={{ color: "#6B6250", textAlign: "center", mt: 0.5 }}
          >
            {t("courseDetails.infoBox.germanTime")}
          </Typography>
        </Box>

        {/* Start Date */}
        <Box className="course-info-cell" sx={{ display: "flex", flexDirection: "column", gap: 0.8 }}>
          <Typography
            fontWeight={800}
            fontSize={{ xs: 12, sm: 13, md: 14 }}
            color="#3A321B"
            sx={{ textAlign: "center" }}
          >
            {t("courseDetails.infoBox.startDate")}
          </Typography>
          <Typography
            fontSize={{ xs: 12, sm: 13, md: 16 }}
            fontWeight={400}
            sx={{ color: "#333", textAlign: "center" }}
          >
            {formattedStartDate}
          </Typography>
          <Typography
            fontWeight={800}
            fontSize={{ xs: 12, sm: 13, md: 14 }}
            color="#3A321B"
            sx={{ textAlign: "center", mt: 0.8 }}
          >
            {t("courseDetails.infoBox.endDate")}
          </Typography>
          <Typography
            fontSize={{ xs: 12, sm: 13, md: 16 }}
            fontWeight={400}
            sx={{ color: "#333", textAlign: "center" }}
          >
            {formattedEndDate}
          </Typography>
          <Typography
            fontWeight={800}
            fontSize={{ xs: 12, sm: 13, md: 14 }}
            color="#3A321B"
            sx={{ textAlign: "center", mt: 0.8 }}
          >
            {t("courseDetails.infoBox.duration")}
          </Typography>
          <Typography
            fontSize={{ xs: 12, sm: 13, md: 16 }}
            fontWeight={400}
            sx={{ color: "#333", textAlign: "center" }}
          >
            {hasDuration ? `${duration} ${t("courseDetails.infoBox.weeks")}` : "-"}
          </Typography>
        </Box>

        {/* Study Method */}
        <Box className="course-info-cell" sx={{ display: "flex", flexDirection: "column", gap: 0.8 }}>
          <Typography
            fontWeight={800}
            fontSize={{ xs: 12, sm: 13, md: 14 }}
            color="#3A321B"
            sx={{ textAlign: "center" }}
          >
            {t("courseDetails.infoBox.studyMethod")}
          </Typography>
          <Typography
            fontSize={{ xs: 12, sm: 13, md: 16 }}
            fontWeight={400}
            sx={{ color: "#333", textAlign: "center" }}
          >
            {t("courseDetails.infoBox.studyMethodValue")}
          </Typography>
          {!specialCourseCustomization && audienceDetails}
        </Box>

        {/* Course books */}
        <Box className="course-info-cell" sx={{ display: "flex", flexDirection: "column", gap: 0.8 }}>
          {specialCourseCustomization && hasSpecialCourseInfo ? (
            audienceDetails
          ) : courseBooks.length > 0 && (
            <Box sx={{ width: "100%" }}>
              <Typography
                fontSize={{ xs: 12, sm: 12, md: 13 }}
                fontWeight={700}
                sx={{ color: "#3A321B", textAlign: "center", mb: 0.5 }}
              >
                {t("courseDetails.infoBox.courseBooks")}
              </Typography>
              <Box
                sx={{
                  m: 0,
                  color: "#333",
                  textAlign: "center",
                }}
              >
                {courseBooks.map((book) => (
                  <Typography
                    component="div"
                    key={book}
                    fontSize={{ xs: 11, sm: 12, md: 12 }}
                    fontWeight={500}
                    sx={{
                      lineHeight: 1.45,
                      overflowWrap: "anywhere",
                      textAlign: "center",
                      width: "100%",
                      direction: "ltr",
                      unicodeBidi: "isolate",
                    }}
                  >
                    {book}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}
        </Box>

        {/* Enrolled / maximum students */}
        <Box className="course-info-cell" sx={{ display: "flex", flexDirection: "column", gap: 0.8 }}>
          <Typography
            fontWeight={800}
            fontSize={{ xs: 12, sm: 13, md: 14 }}
            color="#3A321B"
            sx={{ textAlign: "center" }}
          >
            {t("courseDetails.infoBox.joined")}
          </Typography>
          <Typography
            fontSize={{ xs: 12, sm: 13, md: 16 }}
            fontWeight={600}
            sx={{ color: "#333", textAlign: "center" }}
          >
            {hasCapacity ? `${enrolledStudents}/${maxStudents}` : "-"}
          </Typography>
          {hasCapacity && (
            <Typography
              fontSize={{ xs: 11, sm: 12, md: 13 }}
              fontWeight={500}
              sx={{ color: remainingStudents === 0 ? "error.main" : "text.secondary", textAlign: "center" }}
            >
              {t("courseDetails.infoBox.remaining")}: {remainingStudents}
            </Typography>
          )}
        </Box>

      </Box>

        {/* Teacher profile sits outside the course information table. */}
        {compact && (teacherName || teacherPhotoSrc) && (
          <Box
            onClick={onTeacherClick}
            role={onTeacherClick ? "button" : undefined}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.4,
              cursor: onTeacherClick ? "pointer" : "default",
              alignItems: "center",
              justifyContent: "center",
              mx: { xs: "auto", md: 0 },
              px: { xs: 1, md: 2.5 },
              minWidth: { md: 170 },
              flexShrink: 0,
            }}
          >
            <Typography
              fontWeight={600}
              fontSize={{ xs: 10, sm: 11, md: 13 }}
              color="#8E8E8E"
              sx={{ textAlign }}
            >
              {t("courseDetails.teacherProfile.title", { defaultValue: "Teacher" })}
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.65,
              }}
            >
              {hasTeacherPhoto ? (
                <Box
                  component="img"
                  src={teacherPhotoSrc}
                  alt={teacherName}
                  onError={onTeacherImageError}
                  sx={{
                    width: 100,
                    height: 100,
                    objectFit: "cover",
                    borderRadius: "50%",
                    border: "2px solid #fff",
                    boxShadow: "0 4px 10px rgba(0,0,0,0.12)",
                    bgcolor: "#F6F8F8",
                    flexShrink: 0,
                  }}
                />
              ) : (
                <Avatar
                  sx={{
                    width: 100,
                    height: 100,
                    bgcolor: "#FFD600",
                    color: "#111",
                    fontWeight: 900,
                    border: "2px solid #fff",
                    boxShadow: "0 4px 10px rgba(0,0,0,0.12)",
                    flexShrink: 0,
                    fontSize: 14,
                  }}
                >
                  {teacherFirstName?.[0]}
                  {teacherLastName?.[0]}
                </Avatar>
              )}
              <Typography
                fontSize={{ xs: 12, sm: 13, md: 15 }}
                fontWeight={800}
                sx={{ color: "#111", textAlign: "center" }}
              >
                {teacherName || "-"}
              </Typography>
            </Box>
          </Box>
        )}

      </Box>
      </Box>
    </Box>
  );
};

export default CourseInfoBox;
